import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from './cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
export type ButtonSize = 'sm' | 'md' | 'lg';
export type ButtonShape = 'rounded' | 'pill';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  isLoading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-primary text-primary-foreground hover:opacity-90 focus-visible:ring-primary disabled:opacity-50 shadow-[var(--shadow-pill,none)]',
  secondary:
    'bg-surface text-foreground border border-border hover:bg-border/40 focus-visible:ring-primary disabled:opacity-50',
  ghost:
    'bg-transparent text-foreground hover:bg-surface focus-visible:ring-primary disabled:opacity-50',
  danger:
    'bg-danger text-white hover:opacity-90 focus-visible:ring-danger disabled:opacity-50',
  outline:
    'bg-transparent text-foreground border border-border hover:bg-surface focus-visible:ring-primary disabled:opacity-50',
};

function sizeShapeClasses(size: ButtonSize, shape: ButtonShape): string {
  const radius = shape === 'pill' ? 'rounded-full' : size === 'sm' ? 'rounded-sm' : 'rounded-md';
  switch (size) {
    case 'sm':
      return `h-8 px-3 text-sm gap-1.5 ${radius}`;
    case 'lg':
      return `h-12 px-6 text-base gap-2 ${radius}`;
    case 'md':
    default:
      return `h-9 px-4 text-sm gap-2 ${radius}`;
  }
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    shape = 'rounded',
    isLoading = false,
    leftIcon,
    rightIcon,
    fullWidth = false,
    className,
    children,
    disabled,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      className={cn(
        'inline-flex items-center justify-center font-semibold transition-colors whitespace-nowrap',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed',
        variantClasses[variant],
        sizeShapeClasses(size, shape),
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {isLoading ? (
        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      ) : (
        leftIcon
      )}
      {children}
      {!isLoading && rightIcon}
    </button>
  );
});
